import { useEffect, useLayoutEffect, useRef, useState } from "react";
import LangDropdown from "../../LangDropdown";
import CartNavButton from "../webshop/CartNavButton";
import { icons } from "../../assets";
import { navHrefs, navIds } from "../../translations";

function LandingNav({ lang, onLangChange, t, hrefPrefix = "", onOpenCart }) {
  const [activeNav, setActiveNav] = useState("home");
  const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false });
  const [contactCollapsed, setContactCollapsed] = useState(false);
  const navRef = useRef(null);
  const linkRefs = useRef({});

  function navHref(id) {
    return `${hrefPrefix}${navHrefs[id]}`;
  }

  useEffect(() => {
    let frame = 0;
    const lockedUntilRef = { current: 0 };
    const lockedIdRef = { current: null };

    function sectionEl(navId) {
      return (
        document.querySelector(`[data-nav-section="${navId}"]`) ||
        document.getElementById(navId === "menu" ? "etlap" : navId)
      );
    }

    function pickActiveFromScroll() {
      if (lockedIdRef.current && performance.now() < lockedUntilRef.current) {
        setActiveNav(lockedIdRef.current);
        return;
      }
      lockedIdRef.current = null;

      // Pages without the landing sections (webshop, checkout) highlight nothing.
      if (!navIds.some((id) => sectionEl(id))) {
        setActiveNav(null);
        return;
      }

      const navHeight =
        document.querySelector(".landing-nav")?.getBoundingClientRect().height ||
        72;
      const probe = navHeight + 40;
      let current = null;

      for (const id of navIds) {
        const el = sectionEl(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= probe && rect.bottom > probe) {
          current = id;
          break;
        }
      }

      if (!current) {
        current = navIds[0];
        for (const id of navIds) {
          const el = sectionEl(id);
          if (!el) continue;
          if (el.getBoundingClientRect().top <= probe) current = id;
        }
      }

      setActiveNav((prev) => (prev === current ? prev : current));
    }

    function onScrollOrResize() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(pickActiveFromScroll);
    }

    function onNavClickCapture(event) {
      const anchor = event.target.closest?.("a[href*='#']");
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      const hashIndex = href.indexOf("#");
      if (hashIndex === -1) return;
      const hash = href.slice(hashIndex + 1);
      const navId =
        hash === "etlap"
          ? "menu"
          : navIds.includes(hash)
            ? hash
            : null;
      if (!navId) return;
      lockedIdRef.current = navId;
      lockedUntilRef.current = performance.now() + 900;
      setActiveNav(navId);
    }

    const hash = window.location.hash.replace("#", "");
    if (hash === "etlap" || hash === "menu") {
      setActiveNav("menu");
    } else if (navIds.includes(hash)) {
      setActiveNav(hash);
    } else {
      pickActiveFromScroll();
    }

    document.addEventListener("click", onNavClickCapture, true);
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("click", onNavClickCapture, true);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, []);

  useLayoutEffect(() => {
    const nav = navRef.current;
    const contact = linkRefs.current.contact;
    if (!nav || !contact) return;

    function checkContactFit() {
      const wasCollapsed = contact.classList.contains("is-collapsed");
      contact.classList.remove("is-collapsed");
      void nav.offsetWidth;

      const overflow = nav.scrollWidth - nav.clientWidth;
      const shouldCollapse = wasCollapsed ? overflow > 8 : overflow > 2;
      contact.classList.toggle("is-collapsed", shouldCollapse);
      setContactCollapsed(shouldCollapse);
    }

    checkContactFit();
    const observer = new ResizeObserver(checkContactFit);
    observer.observe(nav);
    const inner = nav.closest(".landing-nav-inner");
    if (inner) observer.observe(inner);
    window.addEventListener("resize", checkContactFit);
    if (document.fonts?.ready) {
      document.fonts.ready.then(checkContactFit).catch(() => {});
    }

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", checkContactFit);
    };
  }, [lang, t.nav]);

  useLayoutEffect(() => {
    function measure() {
      const nav = navRef.current;
      const link = linkRefs.current[activeNav];
      if (!nav || !link) {
        setIndicator((prev) => (prev.ready ? { ...prev, ready: false, width: 0 } : prev));
        return;
      }
      const linkBox = link.getBoundingClientRect();
      if (linkBox.width < 1 || getComputedStyle(link).display === "none") {
        setIndicator((prev) => ({ ...prev, ready: false, width: 0 }));
        return;
      }
      const navBox = nav.getBoundingClientRect();
      setIndicator({
        left: linkBox.left - navBox.left + nav.scrollLeft,
        width: linkBox.width,
        ready: true,
      });
    }

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [activeNav, lang, t.nav, contactCollapsed]);

  return (
    <header className="landing-nav">
      <div className="landing-nav-inner container">
        <a
          href={navHref("home")}
          className="brand"
          aria-label={t.brandAria}
          onClick={() => setActiveNav("home")}
        >
          <img src={icons.logoWhite} alt={t.logoAlt} className="brand-logo" />
        </a>

        <nav className="nav" aria-label="Primary" ref={navRef}>
          {navIds.map((id) => (
            <a
              key={id}
              ref={(el) => {
                linkRefs.current[id] = el;
              }}
              href={navHref(id)}
              className={[
                "nav-link",
                id === "contact" ? "nav-link--contact" : "",
                id === "contact" && contactCollapsed ? "is-collapsed" : "",
                activeNav === id ? "active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-current={activeNav === id ? "true" : undefined}
              onClick={() => setActiveNav(id)}
            >
              {t.nav[id]}
            </a>
          ))}
          <span
            className={
              indicator.ready ? "nav-indicator is-ready" : "nav-indicator"
            }
            aria-hidden="true"
            style={{
              transform: `translateX(${indicator.left}px)`,
              width: `${indicator.width}px`,
            }}
          />
        </nav>

        <div className="topbar-actions">
          <CartNavButton t={t.shop} lang={lang} onOpenCart={onOpenCart} />
          <LangDropdown
            lang={lang}
            onChange={onLangChange}
            ariaLabel={t.langLabel}
            variant="dark"
          />
        </div>
      </div>
    </header>
  );
}

export default LandingNav;
